import React, { useState, useContext } from 'react';
import EDListItem, {
  EDListItemButton,
} from '../../../../../../../../../BaseComponents/List/Components/EDListItem';
import EDListItemText from '../../../../../../../../../BaseComponents/List/Components/EDListItemText';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { ISelection } from '../../../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import EkonomiskAnalysSideMenuIconButton from '../IconButton/EkonomiskAnalysSideMenuIconButton';
import Dialog from '../../../../../../../../../BaseComponents/Dialog/EDDialog';
import EkonomiskAnalysKodbehorighetSelection from './Components/EkonomiskAnalysKodbehorighetSelection/EkonomiskAnalysKodbehorighetSelection';
import { ISelectedAnalys, IGrundurval } from '../../../../EkonomiskAnalysPage';
import { EkonomiskAnalysSideMenuContext } from '../../Context/EkonomiskAnalysSideMenuContext';
import {
  IColumn,
  ISumCategory,
} from '../../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { EkonomiskAnalysContext } from '../../../Context/EkonomiskAnalysContext';
import { IEkonomiskAnalysFilledDates } from '../../../AnalysSettingsHandler/AnalysSettingsHandler';
import { ModelContext } from '../../../../../../../../../Model/ModelContext/ModelContext';
import { KodplanContext } from '../../../../../../../../../Components/KodplanManager/Context/KodplanContext';
import { IStartAnalysInfo } from '../../EkonomiskAnalysSelect/Components/FavoriteAnalysDialog/Queries/ChangeStartAnalysMutation/ChangeStartAnalysMutation';
import { setNewAnalys, NEW_ANALYS_RAPPORT_ID } from './CreateNewAnalysUtils';

interface IProps {
  availableReports: ISelection[];
  sideMenuIsOpen: boolean;
  availableCategories: ISumCategory[];
  dates: IEkonomiskAnalysFilledDates;
  grundurval?: IGrundurval;
  dialogIsOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  startAnalys?: IStartAnalysInfo;
  newAnalysColumns: IColumn[];
  disabled?: boolean;
  disabledReason?: string;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      listItem: {
        paddingTop: 0,
        paddingBottom: 1,
        outline: 0,
      },
      listItemSideMenuOpen: {
        paddingTop: 11,
        paddingBottom: 11,
        paddingLeft: 24,
        marginBottom: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      iconSideMenuOpen: { marginLeft: -4 },
    } as const)
);

/**
 * Komponent för att öppna en dialog där en ny analys kan skapas
 */
const EkonomiskAnalysCreateNewListItem: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = ({
  sideMenuIsOpen,
  availableReports,
  availableCategories,
  dates,
  grundurval,
  dialogIsOpen,
  closeDialog,
  openDialog,
  newAnalysColumns,
  disabled,
  disabledReason,
}) => {
  const { classes, cx } = useStyles();
  const analysFromSelection = (selection: ISelection): ISelectedAnalys => {
    return {
      analys: {
        name: 'Namnlös Analys',
        id: NEW_ANALYS_RAPPORT_ID,
        utdelningLopnr: 1,
        grundurval: grundurval,
      },
      codePrivilege: {
        name: selection.name,
        id: selection.id,
        anvandsFor: selection.anvandsFor,
        analysList: selection.analysList,
      },
    };
  };

  const getInitialAnalys = (): ISelection => {
    const helaHuvudboken = availableReports.find(
      x => x.name === 'Hela huvudboken'
    );
    return helaHuvudboken ? helaHuvudboken : availableReports[0];
  };

  const initialAnalys =
    availableReports.length > 0
      ? analysFromSelection(getInitialAnalys())
      : {
          analys: { name: '', id: '', utdelningLopnr: 1 },
          codePrivilege: { name: '', id: '' },
        };

  const analysChange = (kod: string) => {
    const codePrivilege = availableReports.filter(x => x.id === kod)[0];
    setAnalys(analysFromSelection(codePrivilege));
  };

  const openNewAnalys = () => {
    if (availableReports.length > 1) {
      openDialog();
    } else {
      setNewAnalys(
        availableCategories,
        standardColumns,
        {
          from: { ...dates.from, userString: '' },
          to: { ...dates.to, userString: '' },
        },
        avenyCollectionMap,
        analys,
        createNewAnalys,
        newAnalysColumns,
        activeKodplan
      );
    }
  };

  const closeNewAnalys = () => {
    closeDialog();
    setNewAnalys(
      availableCategories,
      standardColumns,
      {
        from: { ...dates.from, userString: '' },
        to: { ...dates.to, userString: '' },
      },
      avenyCollectionMap,
      analys,
      createNewAnalys,
      newAnalysColumns,
      activeKodplan
    );
  };

  const { standardColumns } = useContext(EkonomiskAnalysContext);
  const { createNewAnalys } = useContext(EkonomiskAnalysSideMenuContext);
  const { activeKodplan } = useContext(KodplanContext);
  const { avenyCollectionMap } = useContext(ModelContext);
  const [analys, setAnalys] = useState(initialAnalys);

  return (
    <>
      {sideMenuIsOpen ? (
        <EDListItemButton
          key={'selectButton'}
          onClick={openNewAnalys}
          className={cx(classes.listItem, {
            [classes.listItemSideMenuOpen]: sideMenuIsOpen,
          })}
          disabled={disabled}
        >
          <EkonomiskAnalysSideMenuIconButton
            sideMenuOpen={sideMenuIsOpen}
            tooltipText="Skapa ny"
            iconType={'add'}
            color={disabled ? 'disabled' : 'primary'}
            onClick={openNewAnalys}
            disabled={disabled}
            disabledReason={disabledReason}
          />
          <EDListItemText
            primary={<strong>Skapa ny</strong>}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </EDListItemButton>
      ) : (
        <EDListItem
          key={'select'}
          onClick={sideMenuIsOpen ? openNewAnalys : undefined}
          className={cx(classes.listItem, {
            [classes.listItemSideMenuOpen]: sideMenuIsOpen,
          })}
          disabled={disabled}
        >
          <EkonomiskAnalysSideMenuIconButton
            sideMenuOpen={sideMenuIsOpen}
            tooltipText="Skapa ny"
            iconType={'add'}
            color={disabled ? 'disabled' : 'primary'}
            onClick={openNewAnalys}
            disabled={disabled}
            disabledReason={disabledReason}
          />
          <EDListItemText
            primary={<strong>Skapa ny</strong>}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </EDListItem>
      )}
      <Dialog
        title="Skapa ny analys"
        open={dialogIsOpen}
        onClose={closeDialog}
        footerActions={[
          { label: 'Avbryt', onClick: closeDialog },
          {
            label: 'Använd',
            onClick: closeNewAnalys,
            buttonProps: {
              disabled: !analys.codePrivilege.id,
              disabledReason: 'Ingen kodbehörighet vald',
            },
          },
        ]}
      >
        <EkonomiskAnalysKodbehorighetSelection
          kod={analys.codePrivilege.id}
          availableReports={availableReports}
          onChange={analysChange}
        />
      </Dialog>
    </>
  );
};

export default EkonomiskAnalysCreateNewListItem;
